// vi: filetype=javascript.jsx
import { T } from "@classdojo/web";
import LoadingMojo from "@classdojo/web/components/misc/LoadingMojo";
import { RAW_cssValue, ThemeUIStyleObject } from "@classdojo/web/nessie/stylingLib";
import { translate } from "@classdojo/web/utils/translate";
import React from "react";
import { Student } from "src/pods/students";
import logEvent from "src/utils/logEvent";

type RandomDisplayProps = {
  displayRandomStudent: (students?: Student[]) => void;
  student?: Student;
  students?: Student[];
  isRestart?: boolean;
  fullscreen?: boolean;
};

type RandomDisplayState = {
  student?: Student;
};

// eslint-disable-next-line react-prefer-function-component/react-prefer-function-component
class RandomDisplay extends React.Component<RandomDisplayProps, RandomDisplayState> {
  constructor(props: RandomDisplayProps) {
    super(props);
    this.state = {
      student: props.student,
    };
  }

  componentDidMount() {
    if (this.state.student && !this.props.isRestart) {
      this.animate();
    }

    logEvent({
      eventName: "random.result.open",
    });
  }

  componentDidUpdate(prevProps: RandomDisplayProps) {
    if (this.props.student !== prevProps.student) {
      this.animate();
    }
  }

  componentWillUnmount() {
    logEvent({
      eventName: "random.result.close",
    });
  }

  render() {
    const { student } = this.state;
    const { fullscreen, students } = this.props;

    if (!students || !student) {
      return (
        <section sx={makeCardStyle(fullscreen)}>
          <LoadingMojo inline text={translate("dojo.toolkit:students.loading")} />
        </section>
      );
    }

    return (
      <div sx={centered}>
        {student && (
          <div sx={containerStyle}>
            <section sx={makeCardStyle(fullscreen)}>
              <img sx={avatarStyle} src={student.avatar} role="presentation" />
              <p sx={nameStyle} role="status" aria-live="polite">
                <T
                  str="dojo.toolkit:student_name_with_last_initial"
                  subs={{
                    firstName: student.firstName,
                    lastName: student.lastName,
                    lastInitial: student.lastName.slice(0, 1),
                  }}
                  fallback="__firstName__ __lastInitial__."
                />
              </p>
            </section>
            <div sx={pickStyle}>
              <button
                data-name="random_display:render:random_pick_again"
                sx={pickButtonStyle(fullscreen)}
                onClick={this.tryAgain}
              >
                {translate({
                  str: "dojo.toolkit:random.pick_again",
                  fallback: "Pick again",
                })}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  _timer: NodeJS.Timeout;

  animate() {
    clearInterval(this._timer);

    const { student: selectedStudent, students } = this.props;

    const MAX_CARDS = 6;
    const ANIMATION_DURATION = 1500;
    let card = 0;

    const nextFrame = () => {
      if (card++ < MAX_CARDS) {
        const currentStudent = this.state.student;
        const otherStudents = students?.filter((student) => student !== currentStudent);
        this.setState({
          student: otherStudents?.[Math.floor(Math.random() * otherStudents.length)],
        });
      } else {
        this.setState({ student: selectedStudent });
        clearInterval(this._timer);
      }
    };

    nextFrame();
    this._timer = setInterval(nextFrame, ANIMATION_DURATION / MAX_CARDS);
  }

  tryAgain = () => {
    if (this.props.students && this.props.students.length === 0) return;
    this.props.displayRandomStudent(this.props.students);
    logEvent({
      eventName: "random.pickagain",
    });
  };
}
export { RandomDisplay };
export default RandomDisplay;

const centered: ThemeUIStyleObject = {
  height: "100%",
  display: "flex",
  alignItems: "center",
};

const containerStyle: ThemeUIStyleObject = {
  width: "100%",
};

const makeCardStyle = (fullscreen?: boolean): ThemeUIStyleObject => ({
  width: fullscreen ? "536px" : "100%",
  margin: RAW_cssValue("0 auto"),
  borderRadius: "dt_radius_s",
  textAlign: "center",
  backgroundColor: RAW_cssValue("#fff"),
  color: RAW_cssValue("#000"),
  padding: RAW_cssValue(fullscreen ? 45 : 0),
});

const nameStyle: ThemeUIStyleObject = {
  width: "100%",
  margin: RAW_cssValue("0"),
  padding: RAW_cssValue("0"),
  fontSize: "32px",
  fontWeight: 600,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const avatarStyle: ThemeUIStyleObject = {
  height: "154px",
  marginBottom: RAW_cssValue("38px"),
};

const pickStyle: ThemeUIStyleObject = {
  padding: RAW_cssValue("20px"),
};

const pickButtonStyle = (fullscreen?: boolean): ThemeUIStyleObject => ({
  width: "100%",
  height: "64px",
  margin: RAW_cssValue("auto"),
  lineHeight: "60px",
  border: fullscreen ? "none" : "dt_active",
  borderRadius: "dt_radius_s",
  fontSize: "20px",
  fontWeight: 800,
  textAlign: "center",
  verticalAlign: "middle",
  color: fullscreen ? "dt_content_light" : "dt_content_accent",
  cursor: "pointer",
  backgroundColor: "transparent",
});
